import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { queryStore } from "../../../../../packages/api/src/queryStore";
import { hubMutations } from "@surgia/api/src/queryStore/hub";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import { useTranslation } from "react-i18next";
import mailIcon from "@surgia/ui/icons/popup/email.svg"; // adjust path as needed
import { countries } from "../../../../../packages/contract/constants/countryCodes";
import sortIcon from "@surgia/ui/icons/table/sort.svg"; // for dropdown arrow
import { useState, useRef } from "react";
import { RequestData, StorefrontDTO } from "../../../../../packages/contract/dtos";
import { zodResolver } from "@hookform/resolvers/zod";
import { registerSchema, RegisterFormValues } from "./validation"; // adjust path as needed
import { useNavigate } from "react-router-dom";
import { Listbox } from "@headlessui/react";

const institutionTypes = [
  { value: "HOSPITAL", labelKey: "shared.entityType.HOSPITAL" },
  { value: "POLY_CLINIC", labelKey: "shared.entityType.POLY_CLINIC" },
  { value: "OTHER", labelKey: "shared.entityType.OTHER" },
];

export function RegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countries[1]);
  const [selectedStorefront, setSelectedStorefront] = useState<StorefrontDTO | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    reset: resetForm,
    formState: { errors },
    watch,
    clearErrors,
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema(t)),
    mode: "onChange",
    defaultValues: {
      name: "",
      type: "",
      country_id: "",
      city_id: "",
      contact_person_first_name: "",
      contact_person_last_name: "",
      contact_person_email: "",
      contact_person_mobile: "",
      mobile_number_prefix: "+966",
    },
  });

  // Fetch storefront data from API
  const { data: storefronts, isLoading: storefrontsLoading } = useQuery({
    ...lookupQueries.queries.storefront(),
    staleTime: 600000,
    retry: false,
  });

  // Use storefronts from API
  const storefrontData: StorefrontDTO[] = storefronts || [];

  // Fetch cities based on selected storefront
  const { data: citiesResponse, isLoading: citiesLoading } = useQuery({
    ...queryStore.shared.lookup.queries.cities({
      sort: "name,asc",
      size: 100,
      page: 0,
      country_id: selectedStorefront?.country?.id || 191,
    }),
    staleTime: 600000,
    enabled: !!selectedStorefront?.country?.id,
  });

  // Extract cities from the response
  const cities = citiesResponse?.data;

  // Watch form values for listbox selections
  const selectedType = watch("type");

  // Find the selected institution type object
  const selectedInstitutionType = institutionTypes.find(
    (type) => type.value === selectedType
  );

  // Use the new request mutation from query store
  const createRequestMutation = useMutation(hubMutations.request.create());

  const onSubmit = (data: RegisterFormValues) => {
    if (!navigator.onLine) {
      toast.error(t("hub.register.offlineError"));
      return;
    }

    // Manual phone validation
    const country = countries.find(
      (c: any) => c.dial === data.mobile_number_prefix
    );
    if (
      country &&
      !country.regex.test(data.contact_person_mobile || "") &&
      (data.contact_person_mobile?.length ?? 0) > 0
    ) {
      setError("contact_person_mobile", {
        type: "manual",
        message: t("shared.branchForm.invalidPhone"),
      });
      return;
    }

    const phoneNumber = data.contact_person_mobile;
    const prefix = data.mobile_number_prefix;
    const cleanNumber = phoneNumber.replace(/^\+/, "");

    // Create the final data object with the formatted phone number
    const formattedData: RequestData = {
      name: data.name,
      type: data.type!,
      city_id: parseInt(data.city_id!),
      contact_person_first_name: data.contact_person_first_name,
      contact_person_last_name: data.contact_person_last_name,
      contact_person_email: data.contact_person_email,
      contact_person_mobile: prefix + cleanNumber,
      mobile_number_prefix: prefix,
      country_id: selectedStorefront?.country?.id?.toString() || "191",
      storefront_id: selectedStorefront?.storefront_id || 0,
    };

    createRequestMutation.mutate(formattedData, {
      onSuccess: () => {
        resetForm();
        toast.success(t("hub.register.successMessage"));
        navigate("/login");
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.errorCodeDescription ||
          "An error occurred";
        if (errorMessage.includes("name")) {
          toast.error(t("hub.register.nameExists"));
        } else if (errorMessage.includes("email")) {
          toast.error(t("hub.register.emailExists"));
        } else if (errorMessage.includes("mobile")) {
          toast.error(t("hub.register.mobileExists"));
        }
      },
    });
  };
  return (
    <form style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }} onSubmit={handleSubmit(onSubmit)}>
      {/* Institution name */}
      <div style={{ gridColumn: "span 2" }}>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px" }}>
          {t("hub.formFields.clinicHospitalName")}{" "}
          <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          {...register("name")}
          style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 16px" }}
          placeholder={t("hub.placeholders.clinicHospitalName")}
        />
        {errors.name && (
          <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>{errors.name.message}</p>
        )}
      </div>

      {/* Institution type - Listbox */}
      <div style={{ gridColumn: "span 2" }}>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px" }}>
          {t("hub.formFields.clinicHospitalType")}{" "}
          <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <Listbox
          value={selectedInstitutionType || null}
          onChange={(selectedType) => {
            setValue("type", selectedType?.value || "");
            clearErrors("type");
          }}
        >
          <div style={{ position: "relative" }}>
            <Listbox.Button style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 16px", textAlign: "left", backgroundColor: "white", outline: "none", color: "#6b7280" }}>
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {selectedInstitutionType
                  ? t(selectedInstitutionType.labelKey)
                  : t("hub.placeholders.clinicHospitalType")}
              </span>
              <span style={{ position: "absolute", top: 0, bottom: 0, right: 0, display: "flex", alignItems: "center", paddingRight: "8px", pointerEvents: "none" }}>
                <svg
                  style={{ width: "20px", height: "20px", color: "#9ca3af" }}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </Listbox.Button>

            <Listbox.Options style={{ position: "absolute", zIndex: 1000, marginTop: "4px", width: "100%", backgroundColor: "white", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", maxHeight: "240px", borderRadius: "6px", padding: "4px 0", fontSize: "16px", border: "1px solid rgba(0, 0, 0, 0.1)", overflow: "auto", outline: "none" }}>
              {institutionTypes.map((type) => (
                <Listbox.Option
                  key={type.value}
                  value={type}
                  className=""
                >
                  {({ selected, active }: { selected: boolean; active: boolean }) => (
                    <div style={{
                      position: "relative",
                      cursor: "default",
                      userSelect: "none",
                      padding: "8px 16px 8px 40px",
                      backgroundColor: active ? "#dbeafe" : "transparent",
                      color: active ? "#1e3a8a" : "#111827",
                    }}>
                      <span
                        style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: selected ? "500" : "400" }}
                      >
                        {t(type.labelKey)}
                      </span>
                      {selected ? (
                        <span style={{ position: "absolute", top: 0, bottom: 0, left: 0, display: "flex", alignItems: "center", paddingLeft: "12px", color: "#2563eb" }}>
                          <svg
                            style={{ width: "20px", height: "20px" }}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
        {errors.type && (
          <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>{errors.type.message}</p>
        )}
      </div>

      {/* Storefront/Country */}
      <div style={{ gridColumn: "span 2" }}>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px" }}>
          {t("hub.formFields.country")} <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <Listbox
          value={selectedStorefront || null}
          onChange={(storefront) => {
            setSelectedStorefront(storefront);
            setValue("country_id", storefront?.country?.id?.toString() || "");
            setValue("city_id", ""); // Reset city when country changes
            
            // Auto-set mobile number prefix based on selected storefront's country
            if (storefront?.country?.mobile_code) {
              const matchingCountry = countries.find(
                (c: any) => c.dial === storefront.country.mobile_code
              );
              if (matchingCountry) {
                setSelectedCountry(matchingCountry);
                setValue("mobile_number_prefix", storefront.country.mobile_code);
              }
            }
            
            clearErrors("country_id");
            clearErrors("city_id");
          }}
          disabled={storefrontsLoading}
        >
          <div style={{ position: "relative" }}>
            <Listbox.Button style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              padding: "8px 16px",
              textAlign: "left",
              outline: "none",
              backgroundColor: storefrontsLoading ? "#f3f4f6" : "white",
              color: storefrontsLoading ? "#9ca3af" : "#6b7280",
              cursor: storefrontsLoading ? "not-allowed" : "pointer",
            }}>
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {storefrontsLoading 
                  ? t("hub.placeholders.loadingCities")
                  : selectedStorefront
                    ? selectedStorefront.country.name
                    : t("hub.placeholders.country")}
              </span>
              <span style={{ position: "absolute", top: 0, bottom: 0, right: 0, display: "flex", alignItems: "center", paddingRight: "8px", pointerEvents: "none" }}>
                <svg
                  style={{ width: "20px", height: "20px", color: "#9ca3af" }}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </Listbox.Button>

            <Listbox.Options style={{ position: "absolute", zIndex: 1000, marginTop: "4px", width: "100%", backgroundColor: "white", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", maxHeight: "240px", borderRadius: "6px", padding: "4px 0", fontSize: "16px", border: "1px solid rgba(0, 0, 0, 0.1)", overflow: "auto", outline: "none" }}>
              {storefrontData?.map((storefront: StorefrontDTO) => (
                <Listbox.Option
                  key={storefront.storefront_id}
                  value={storefront}
                  className=""
                >
                  {({ selected, active }: { selected: boolean; active: boolean }) => (
                    <div style={{
                      position: "relative",
                      cursor: "default",
                      userSelect: "none",
                      padding: "8px 16px 8px 40px",
                      backgroundColor: active ? "#dbeafe" : "transparent",
                      color: active ? "#1e3a8a" : "#111827",
                    }}>
                      <span
                        style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: selected ? "500" : "400" }}
                      >
                        {storefront.country.name}
                      </span>
                      {selected ? (
                        <span style={{ position: "absolute", top: 0, bottom: 0, left: 0, display: "flex", alignItems: "center", paddingLeft: "12px", color: "#2563eb" }}>
                          <svg
                            style={{ width: "20px", height: "20px" }}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
        {errors.country_id && (
          <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>{errors.country_id.message}</p>
        )}
      </div>

      {/* City */}
      <div style={{ gridColumn: "span 2" }}>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px" }}>
          {t("hub.formFields.city")} <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <Listbox
          value={
            cities?.find(
              (city) => String(city.id) === String(watch("city_id"))
            ) || null
          }
          onChange={(city) => {
            setValue("city_id", city?.id?.toString() || "");
            clearErrors("city_id");
          }}
          disabled={!selectedStorefront || citiesLoading}
        >
          <div style={{ position: "relative" }}>
            <Listbox.Button style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              padding: "8px 16px",
              outline: "none",
              backgroundColor: (!selectedStorefront || citiesLoading) ? "#f3f4f6" : "white",
              color: (!selectedStorefront || citiesLoading) ? "#9ca3af" : "#6b7280",
              cursor: (!selectedStorefront || citiesLoading) ? "not-allowed" : "pointer",
            }}>
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>
                {citiesLoading 
                  ? t("hub.placeholders.loadingCities")
                  : cities?.find(
                      (city) => String(city.id) === String(watch("city_id"))
                    )?.name || t("hub.placeholders.city")
                }
              </span>
              <span style={{ position: "absolute", top: 0, bottom: 0, right: 0, display: "flex", alignItems: "center", paddingRight: "8px", pointerEvents: "none" }}>
                <svg
                  style={{ width: "20px", height: "20px", color: "#9ca3af" }}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </Listbox.Button>

            <Listbox.Options style={{ position: "absolute", zIndex: 1000, marginTop: "4px", width: "100%", backgroundColor: "white", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", maxHeight: "240px", borderRadius: "6px", padding: "4px 0", fontSize: "16px", border: "1px solid rgba(0, 0, 0, 0.1)", overflow: "auto", outline: "none" }}>
              {cities?.map((city) => (
                <Listbox.Option
                  key={city.id}
                  value={city}
                  className=""
                >
                  {({ selected, active }: { selected: boolean; active: boolean }) => (
                    <div style={{
                      position: "relative",
                      cursor: "default",
                      userSelect: "none",
                      padding: "8px 16px 8px 40px",
                      backgroundColor: active ? "#dbeafe" : "transparent",
                      color: active ? "#1e3a8a" : "#111827",
                    }}>
                      <span
                        style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: selected ? "500" : "400" }}
                      >
                        {city.name}
                      </span>
                      {selected ? (
                        <span style={{ position: "absolute", top: 0, bottom: 0, left: 0, display: "flex", alignItems: "center", paddingLeft: "12px", color: "#2563eb" }}>
                          <svg
                            style={{ width: "20px", height: "20px" }}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
        {errors.city_id && (
          <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>{errors.city_id.message}</p>
        )}
      </div>

      {/* Contact person name */}
      <div>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px" }}>
          {t("hub.formFields.firstName")}{" "}
          <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          {...register("contact_person_first_name")}
          style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 16px" }}
          placeholder={t("hub.placeholders.firstName")}
        />
        {errors.contact_person_first_name && (
          <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
            {errors.contact_person_first_name.message}
          </p>
        )}
      </div>
      <div>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px" }}>&nbsp;</label>
        <input
          {...register("contact_person_last_name")}
          style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 16px" }}
          placeholder={t("hub.placeholders.lastName")}
        />
        {errors.contact_person_last_name && (
          <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
            {errors.contact_person_last_name.message}
          </p>
        )}
      </div>

      {/* Contact person Email */}
      <div style={{ gridColumn: "span 2" }}>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px" }}>
          {t("hub.formFields.email")} <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }}>
            <img src={mailIcon} alt="Mail" style={{ width: "20px", height: "20px" }} />
          </span>
          <input
            {...register("contact_person_email")}
            style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 16px 8px 40px" }}
            placeholder={t("hub.placeholders.email")}
            type="email"
          />
        </div>
        {errors.contact_person_email && (
          <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
            {errors.contact_person_email.message}
          </p>
        )}
      </div>

      {/* Contact person number */}
      <div style={{ gridColumn: "span 2" }}>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px" }}>
          {t("hub.register.mobile")} <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <div style={{ display: "flex", borderRadius: "8px" }}>
          <div style={{ position: "relative" }} ref={dropdownRef}>
            <button
              type="button"
              style={{ display: "flex", alignItems: "center", border: "1px solid #d1d5db", borderRight: "none", borderTopLeftRadius: "8px", borderBottomLeftRadius: "8px", padding: "8px 12px", backgroundColor: "white", minWidth: "60px", outline: "none", cursor: "pointer" }}
              onClick={() => setShowDropdown((v) => !v)}
              tabIndex={0}
            >
              <span style={{ fontSize: "20px" }}>{selectedCountry.flag}</span>
              <span style={{ marginLeft: "8px" }}>
                <img src={sortIcon} alt="Sort" style={{ width: "16px", height: "16px" }} />
              </span>
            </button>
            {showDropdown && (
              <div
                style={{ position: "absolute", left: 0, bottom: "100%", marginBottom: "4px", backgroundColor: "white", border: "1px solid #d1d5db", borderRadius: "8px", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", zIndex: 10, minWidth: "100%", width: "max-content" }}
              >
                {countries.map((country: any) => (
                  <div
                    key={country.code}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                      cursor: "pointer",
                      backgroundColor: selectedCountry.dial === country.dial ? "#f3f4f6" : "transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedCountry.dial !== country.dial) {
                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCountry.dial !== country.dial) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                    onClick={() => {
                      setSelectedCountry(country);
                      setValue("mobile_number_prefix", country.dial);
                      setShowDropdown(false);
                    }}
                  >
                    <span style={{ marginRight: "8px", fontSize: "20px" }}>{country.flag}</span>
                    <span style={{ marginRight: "8px", fontSize: "14px", color: "#374151" }}>
                      {t(`shared.branchForm.countries.${country.code}`)}
                    </span>
                    <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: "12px" }}>
                      {country.dial}
                    </span>
                    {selectedCountry.dial === country.dial && (
                      <span style={{ marginLeft: "24px", display: "flex", alignItems: "center" }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <input
            {...register("contact_person_mobile")}
            style={{ width: "100%", borderTop: "1px solid #d1d5db", borderBottom: "1px solid #d1d5db", borderRight: "1px solid #d1d5db", borderLeft: "none", borderTopRightRadius: "8px", borderBottomRightRadius: "8px", padding: "8px 16px", outline: "none" }}
            placeholder={t("hub.placeholders.mobile")}
            type="tel"
          />
        </div>
        {errors.contact_person_mobile && (
          <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
            {errors.contact_person_mobile.message}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={createRequestMutation.isPending}
        style={{
          gridColumn: "span 2",
          padding: "12px",
          backgroundColor: "#7144FF",
          color: "white",
          border: "none",
          borderRadius: "8px",
          cursor: createRequestMutation.isPending ? "not-allowed" : "pointer",
          opacity: createRequestMutation.isPending ? 0.6 : 1,
        }}
        onMouseEnter={(e) => {
          if (!createRequestMutation.isPending) {
            e.currentTarget.style.backgroundColor = "#6B3FE8";
          }
        }}
        onMouseLeave={(e) => {
          if (!createRequestMutation.isPending) {
            e.currentTarget.style.backgroundColor = "#7144FF";
          }
        }}
      >
        {createRequestMutation.isPending
          ? t("hub.register.submitting")
          : t("hub.register.submit")}
      </button>
    </form>
  );
}
